import { Action } from '@reduxjs/toolkit';
import { combineEpics, StateObservable } from 'redux-observable';
import { Observable, of } from 'rxjs';
import {
  pluck, filter, mergeMap, switchMap, mapTo, map, first,
} from 'rxjs/operators';

import {
  DISCONNECTED_FROM_OPERATOR, NET_POSITION, PLAYER_LIMITS_NOTIFICATION, DISCONNECTED, PLAYER_NR_TABLE_LIMIT_REACHED,
} from '@ezugi/constants';

import {
  socketActions,
  videoActions,
  dialogActions,
  notificationActions,
  preloaderActions,
  gameActions,
  userActions,
  roundActions,
} from '../../actions';
import { DISCONNECTED_FROM_OPERATOR_DIALOG, PLAYER_NR_TABLE_LIMIT_REACHED_DIALOG } from '../../constants/dialog';
import {
  notificationTypes,
  ukgcNotifications,
} from '../../constants/regulations';
import {
  currencySelector,
  currentLimitsSelector,
  dialogNameSelector,
  isEvoWrappedSelector,
  isMinimalBetNotificationEnabledSelector,
  minBetSelector,
  videoReadyStatusSelector,
} from '../../selectors';
import { RootState } from '../../types';
import { getRegulationsActions } from './utils';

const { socket } = socketActions;
const { video } = videoActions;
const { dialog } = dialogActions;
const { notification } = notificationActions;
const { preloader } = preloaderActions;
const { game } = gameActions;
const { user } = userActions;
const { round } = roundActions;

const regulationsEpic = (action$: Observable<Action>, state$: StateObservable<RootState>):
Observable<any> => action$.pipe(
  filter(socket.message.match),
  pluck('payload'),
  filter(({ MessageType }) => MessageType === PLAYER_LIMITS_NOTIFICATION && !isEvoWrappedSelector(state$.value)),
  pluck('data'),
  filter(({ type }) => notificationTypes.includes(type) || ukgcNotifications.includes(type)),
  mergeMap((data) => of(...getRegulationsActions(data)))
);

const disconnectFromOperatorEpic = (action$: Observable<Action>) => action$.pipe(
  filter(socket.message.match),
  pluck('payload'),
  filter(({ MessageType }) => MessageType === DISCONNECTED_FROM_OPERATOR),
  mapTo(dialog.add({ name: DISCONNECTED_FROM_OPERATOR_DIALOG }))
);

const checkVideoEpic = (action$: Observable<Action>, state$: StateObservable<RootState>) => action$.pipe(
  filter(video.shouldStop.match),
  switchMap(() => (videoReadyStatusSelector(state$.value)
    ? of(video.stop())
    : action$.pipe(
      filter(video.play.match),
      mergeMap(() => of(video.stop()))
    )))
);

const minimalBetNotificationEpic = (action$: Observable<Action>, state$: StateObservable<RootState>) => action$.pipe(
  filter(preloader.hide.match),
  first(),
  filter(() => {
    const { chips } = currentLimitsSelector(state$.value);
    const minBetValue = minBetSelector(state$.value);
    const defaultChipValue = chips.find((chip) => chip.default)?.value ?? minBetValue;
    const isDefaultChipBiggerThanMinBet = defaultChipValue > minBetValue;

    return isMinimalBetNotificationEnabledSelector(state$.value) && isDefaultChipBiggerThanMinBet;
  }),
  switchMap(() => action$.pipe(
    // if tutorial or any dialog is opened, skip until it is closed
    filter(dialogNameSelector(state$.value) ? dialog.remove.match : game.start.match),
    first()
  )),
  map(() => notification.add({ message: 'notifications.minimal_bet',
    delay: 5000,
    variables: {
      minBet: minBetSelector(state$.value),
      currency: currencySelector(state$.value),
    } })),

);

const netPositionEpic = (action$: Observable<Action>) => action$.pipe(
  filter(socket.message.match),
  map(({ payload }) => payload),
  filter(({ MessageType }) => MessageType === NET_POSITION),
  map(({ NetPosition }) => user.set({ netPosition: NetPosition }))
);

const playerNrTableLimitReachedEpic = (action$: Observable<Action>) => action$.pipe(
  filter(socket.message.match),
  map(({ payload }) => payload),
  filter(({ MessageType }) => MessageType === PLAYER_NR_TABLE_LIMIT_REACHED),
  mergeMap(({ MessageType }) => of(
    dialog.add({ name: PLAYER_NR_TABLE_LIMIT_REACHED_DIALOG }),
    socket.error({ error: MessageType }),
    socket.close(),
    video.stop(),
    round.set({ roundStatus: DISCONNECTED })
  ))
);

export default combineEpics(
  regulationsEpic,
  disconnectFromOperatorEpic,
  checkVideoEpic,
  minimalBetNotificationEpic,
  netPositionEpic,
  playerNrTableLimitReachedEpic
);
